import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaGraduationCap,
  FaCode,
  FaFire,
  FaChartLine,
  FaBookOpen,
  FaCompass,
  FaArrowLeft,
  FaTrophy,
  FaUserGraduate,
  FaCalendarAlt,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const MyLearning = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [courses, setCourses] = useState([]);
  const [overallProgress, setOverallProgress] = useState({
    completed: 0,
    total: 0,
    percentage: 0,
  });
  const [continueTopic, setContinueTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setShowFooter(false);
    fetchMyCourses();
    fetchOverallProgress();
    fetchContinueLearning();
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      setShowFooter(true);
      window.removeEventListener("resize", checkMobile);
    };
  }, [isAuthenticated]);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchMyCourses = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/roadmap/my-courses");
      setCourses(response.data.courses);
    } catch (error) {
      console.error("Fetch my courses error:", error);
      toast.error("Failed to fetch your courses");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverallProgress = async () => {
    try {
      const response = await axios.get("/api/roadmap/overall-progress");
      setOverallProgress(response.data.overallProgress);
    } catch (error) {
      console.error("Fetch overall progress error:", error);
    }
  };

  const fetchContinueLearning = async () => {
    try {
      const response = await axios.get("/api/roadmap/continue-learning");
      setContinueTopic(response.data.continueTopic);
    } catch (error) {
      console.error("Fetch continue learning error:", error);
    }
  };

  const handleContinueCourse = (courseId) => {
    navigate(`/learning/course/${courseId}`);
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getHeaderGradient = () =>
    isDarkMode
      ? "linear-gradient(135deg, #0f0f1a, #1a1a2e)"
      : "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)";

  const getProgressColor = (percentage) => {
    if (percentage >= 75) return "#10b981";
    if (percentage >= 50) return "#3b82f6";
    if (percentage >= 25) return "#f59e0b";
    return "#ef4444";
  };

  if (loading) return <Loader />;

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: getHeaderGradient(),
          padding: isMobile ? "1.5rem" : "2rem",
          borderRadius: "24px",
          marginBottom: "2rem",
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <FaUserGraduate size={isMobile ? 24 : 32} />
            <div>
              <h1
                style={{ fontSize: isMobile ? "1.5rem" : "1.8rem", margin: 0 }}
              >
                My Learning
              </h1>
              <p
                style={{
                  opacity: 0.9,
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  marginTop: "0.3rem",
                }}
              >
                Track your progress and continue learning
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              cursor: "pointer",
              padding: isMobile ? "0.5rem 1rem" : "0.6rem 1.2rem",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: isMobile ? "0.8rem" : "0.85rem",
              fontWeight: "500",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.25)";
              e.target.style.transform = "scale(1.02)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.15)";
              e.target.style.transform = "scale(1)";
            }}
          >
            <FaArrowLeft /> Back to Dashboard
          </button>
        </div>
      </motion.div>

      {/* Overall Progress Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          background: getCardBackground(),
          borderRadius: "20px",
          padding: isMobile ? "1.2rem" : "1.5rem",
          marginBottom: "2rem",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <h3
          style={{
            color: getTextColor(),
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: isMobile ? "1rem" : "1.1rem",
          }}
        >
          <FaChartLine color="#8b5cf6" /> Your Overall Progress
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2rem",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: "12px",
                background: isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress.percentage}%` }}
                transition={{ duration: 1 }}
                style={{
                  height: "100%",
                  background: "linear-gradient(90deg, #8b5cf6, #3b82f6)",
                  borderRadius: "10px",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "0.5rem",
              }}
            >
              <span
                style={{
                  color: getTextSecondary(),
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                }}
              >
                {overallProgress.completed} / {overallProgress.total} topics
                completed
              </span>
              <span
                style={{
                  color: "#8b5cf6",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                }}
              >
                {Math.round(overallProgress.percentage)}%
              </span>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: isMobile ? "70px" : "80px",
                height: isMobile ? "70px" : "80px",
                borderRadius: "50%",
                background: `conic-gradient(#8b5cf6 ${overallProgress.percentage * 3.6}deg, ${isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"} 0deg)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: isMobile ? "55px" : "60px",
                  height: isMobile ? "55px" : "60px",
                  borderRadius: "50%",
                  background: getCardBackground(),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  fontWeight: "bold",
                  color: "#8b5cf6",
                }}
              >
                {Math.round(overallProgress.percentage)}%
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Continue Learning Section - Only shows if enrolled */}
      {continueTopic && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            background: `linear-gradient(135deg, ${isDarkMode ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)"}, ${isDarkMode ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)"})`,
            borderRadius: "20px",
            padding: isMobile ? "1.2rem" : "1.5rem",
            marginBottom: "2rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1rem",
            }}
          >
            <FaFire color="#f59e0b" size={isMobile ? 18 : 20} />
            <h3
              style={{
                color: getTextColor(),
                margin: 0,
                fontSize: isMobile ? "1rem" : "1.1rem",
              }}
            >
              Continue Learning
            </h3>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <h4
                style={{
                  color: getTextColor(),
                  marginBottom: "0.3rem",
                  fontSize: isMobile ? "0.95rem" : "1rem",
                }}
              >
                {continueTopic.title}
              </h4>
              <p
                style={{
                  color: getTextSecondary(),
                  fontSize: isMobile ? "0.75rem" : "0.8rem",
                }}
              >
                {continueTopic.domainId?.name}
              </p>
            </div>
            <button
              onClick={() => handleContinueCourse(continueTopic.domainId?._id)}
              style={{
                padding: isMobile ? "0.6rem 1.2rem" : "0.75rem 1.5rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: "500",
                fontSize: isMobile ? "0.8rem" : "0.85rem",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            >
              Resume <FaArrowRight />
            </button>
          </div>
        </motion.div>
      )}

      {/* Header with Title and Explore Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <h2
          style={{
            color: getTextColor(),
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: isMobile ? "1.2rem" : "1.3rem",
          }}
        >
          <FaBookOpen /> My Enrolled Courses
        </h2>
        <button
          onClick={() => navigate("/learning/explore")}
          style={{
            padding: isMobile ? "0.5rem 1rem" : "0.6rem 1.2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: isMobile ? "0.8rem" : "0.85rem",
            fontWeight: "500",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
          onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
        >
          <FaCompass /> Explore Courses
        </button>
      </div>

      {/* Enrolled Courses Grid */}
      {courses.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            textAlign: "center",
            padding: isMobile ? "2rem" : "3rem",
            background: getCardBackground(),
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaBookOpen size={48} color={getTextSecondary()} />
          <p
            style={{
              color: getTextSecondary(),
              marginTop: "1rem",
              marginBottom: "1rem",
            }}
          >
            You haven't enrolled in any courses yet.
          </p>
          <button
            onClick={() => navigate("/learning/explore")}
            style={{
              padding: "0.6rem 1.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: "500",
              transition: "transform 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
          >
            Explore Courses
          </button>
        </motion.div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {courses.map((course, index) => (
            <motion.div
              key={course._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -5 }}
              onClick={() => handleContinueCourse(course._id)}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                padding: isMobile ? "1.2rem" : "1.5rem",
                border: `1px solid ${getBorderColor()}`,
                cursor: "pointer",
                transition: "all 0.3s",
              }}
            >
              <div
                style={{
                  width: isMobile ? "45px" : "50px",
                  height: isMobile ? "45px" : "50px",
                  background: `${course.color}20`,
                  borderRadius: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <FaCode size={isMobile ? 20 : 24} color={course.color} />
              </div>
              <h3
                style={{
                  color: getTextColor(),
                  marginBottom: "0.3rem",
                  fontSize: isMobile ? "1rem" : "1.1rem",
                }}
              >
                {course.name}
              </h3>
              <p
                style={{
                  color: getTextSecondary(),
                  fontSize: isMobile ? "0.75rem" : "0.8rem",
                  marginBottom: "1rem",
                  minHeight: isMobile ? "40px" : "50px",
                }}
              >
                {course.description?.substring(0, 80)}...
              </p>
              <div>
                <div
                  style={{
                    height: "6px",
                    background: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "#e2e8f0",
                    borderRadius: "5px",
                    overflow: "hidden",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      width: `${course.progress}%`,
                      height: "100%",
                      background: getProgressColor(course.progress),
                      transition: "width 0.5s ease",
                    }}
                  />
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span
                    style={{
                      color: getTextSecondary(),
                      fontSize: isMobile ? "0.65rem" : "0.7rem",
                    }}
                  >
                    {course.completedTopics} / {course.totalTopics} completed
                  </span>
                  <span
                    style={{
                      color: getProgressColor(course.progress),
                      fontSize: isMobile ? "0.65rem" : "0.7rem",
                      fontWeight: "500",
                    }}
                  >
                    {Math.round(course.progress)}%
                  </span>
                </div>
              </div>
              <div style={{ marginTop: "0.8rem" }}>
                <span
                  style={{
                    fontSize: isMobile ? "0.65rem" : "0.7rem",
                    color: getTextSecondary(),
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaCalendarAlt size={10} /> Enrolled:{" "}
                  {new Date(course.enrolledAt).toLocaleDateString()}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default MyLearning;
